import { EngagementScoreTerminationReason, StreamTimelineMetadata } from '@quickplay/player-interface';
import { normalizeTiming, TimingSnapshot, ValidTimingSnapshot } from './EngagementScoreUtils';
import {
  createEngagementBehaviorTracker,
  EngagementBehaviorAggregate,
  EngagementObservation,
  deriveUnmutedActiveRatio,
  deriveVisibilityRatio,
} from './EngagementBehaviorTracker';
import { calculateEngagementScore, createEngagementScoreResult } from './EngagementScore';

interface LiveProgram {
  id?: string;
  startTime: string;
  endTime: string;
  eligibleDuration: number;
}

export interface FinalizedLiveProgramContribution {
  readonly coverage: number;
  readonly activeRatio: number;
  readonly watchedDuration: number;
  readonly pauseDuration: number;
  readonly eligibleDuration: number;
  readonly behaviorAggregate: EngagementBehaviorAggregate;
}

interface LiveProgramEngagementScoreTrackerOptions {
  getTimingSnapshot: () => TimingSnapshot | undefined;
  getObservation: () => EngagementObservation;
  onScore: (score: string) => void;
  onSegmentFinalized?: (segment: FinalizedLiveProgramContribution) => void;
}

export function createLiveProgramEngagementScoreTracker({
  getTimingSnapshot,
  getObservation,
  onScore,
  onSegmentFinalized,
}: LiveProgramEngagementScoreTrackerOptions) {
  const behaviorTracker = createEngagementBehaviorTracker({
    getObservation,
  });

  let program: LiveProgram | undefined;
  let baseline: ValidTimingSnapshot | undefined;
  let lastTiming: ValidTimingSnapshot | undefined;
  let active = false;
  let timingInvalid = false;
  let finalized = false;

  return {
    onProgramBoundary(metadata?: StreamTimelineMetadata): void {
      const nextProgram = normalizeProgramMetadata(metadata);
      if (!nextProgram) {
        console.info('[Engagement Score] live-program boundary ignored because metadata is invalid', metadata);
        return;
      }

      console.info(`[Engagement Score] live-program boundary accepted id=${nextProgram.id ?? 'unknown'}`);
      this.finalize('program-boundary');
      this.reset();
      active = true;
      program = nextProgram;
      behaviorTracker.reset();
      establishBaseline();
    },

    observe(): void {
      if (!active || finalized || timingInvalid) {
        return;
      }

      if (!baseline) {
        establishBaseline();
        return;
      }

      const timing = normalizeTiming(getTimingSnapshot());
      if (!timing || timing.playTime < lastTiming!.playTime || timing.pauseTime < lastTiming!.pauseTime) {
        timingInvalid = true;
        return;
      }

      const playTimeDelta = timing.playTime - lastTiming!.playTime;
      behaviorTracker.observe(playTimeDelta);
      lastTiming = timing;
    },

    finalize(terminationReason: EngagementScoreTerminationReason): void {
      if (!active || finalized) {
        console.info(
          `[Engagement Score] live-program finalize skipped active=${active} finalized=${finalized} reason=${terminationReason}`,
        );
        return;
      }

      finalized = true;
      if (!program || !baseline || timingInvalid) {
        console.info(
          `[Engagement Score] live-program finalize skipped program=${Boolean(program)} baseline=${Boolean(baseline)} timingInvalid=${timingInvalid}`,
        );
        return;
      }

      const finalTiming = normalizeTiming(getTimingSnapshot());
      if (
        !finalTiming ||
        finalTiming.playTime < lastTiming!.playTime ||
        finalTiming.pauseTime < lastTiming!.pauseTime
      ) {
        console.info('[Engagement Score] live-program finalize skipped invalid final timing');
        return;
      }

      const watchedDuration = finalTiming.playTime - baseline.playTime;
      const pauseDuration = finalTiming.pauseTime - baseline.pauseTime;
      const coverage = Math.min(watchedDuration / program.eligibleDuration, 1);
      const activeRatio = watchedDuration === 0 ? 0 : watchedDuration / (watchedDuration + pauseDuration);

      const behaviorAggregate = behaviorTracker.snapshot();
      const unmutedMetrics = deriveUnmutedActiveRatio(behaviorAggregate);
      const visibilityRatio = deriveVisibilityRatio(behaviorAggregate);

      const score = calculateEngagementScore({
        coverage,
        activeRatio,
        unmutedActiveRatio: unmutedMetrics.unmutedActiveRatio,
        visibilityRatio,
      });
      if (score === undefined) {
        console.info('[Engagement Score] live-program finalize skipped score undefined');
        return;
      }

      const engagementScore = createEngagementScoreResult({
        scope: 'live-program',
        score,
        coverage,
        activeRatio,
        watchedDuration,
        pauseDuration,
        eligibleDuration: program.eligibleDuration,
        terminationReason,
        program: {
          id: program.id,
          startTime: program.startTime,
          endTime: program.endTime,
        },
        unmutedActiveRatio: unmutedMetrics.unmutedActiveRatio,
        mutedWatchDuration: unmutedMetrics.mutedWatchDuration,
        activePlaybackDuration: unmutedMetrics.activePlaybackDuration,
      });

      onSegmentFinalized?.({
        coverage,
        activeRatio,
        watchedDuration,
        pauseDuration,
        eligibleDuration: program.eligibleDuration,
        behaviorAggregate,
      });

      onScore(engagementScore);
      console.info(`[Engagement Score] live-program emitted ${engagementScore}`);
    },

    reset(): void {
      program = undefined;
      baseline = undefined;
      lastTiming = undefined;
      active = false;
      timingInvalid = false;
      finalized = false;
      behaviorTracker.reset();
    },
  };

  function establishBaseline(): void {
    const timing = normalizeTiming(getTimingSnapshot());
    if (!timing) {
      return;
    }
    baseline = timing;
    lastTiming = timing;
  }
}

function normalizeProgramMetadata(metadata?: StreamTimelineMetadata): LiveProgram | undefined {
  const candidate = isRecord(metadata) ? metadata : undefined;
  const startTime = normalizeNonEmptyString(candidate?.['pgmStartTime']);
  const endTime = normalizeNonEmptyString(candidate?.['pgmEndTime']);
  if (!startTime || !endTime) {
    return undefined;
  }

  const startMs = Date.parse(startTime);
  const endMs = Date.parse(endTime);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return undefined;
  }

  const eligibleDuration = (endMs - startMs) / 1000;
  if (!Number.isFinite(eligibleDuration) || eligibleDuration <= 0) {
    return undefined;
  }

  const id = normalizeNonEmptyString(candidate?.['pgmId']);
  return {
    id,
    startTime,
    endTime,
    eligibleDuration,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeNonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}
